import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createSite,
  deleteSite,
  getSiteDatasets,
  listSites,
  updateSite,
} from "@/api/sites";
import { SIMULATIONS_LIST_QUERY_KEY } from "@/hooks/useSimulationsList";
import type {
  Dataset,
  Site,
  SiteCreate,
  SiteUpdate,
  SiteUpdateResult,
} from "@/types";

/**
 * Clé de cache canonique pour la liste des sites de l'owner courant (compte
 * authentifié via JWT ou session anonyme via cookie). Exposée pour permettre
 * les invalidations ciblées après création/mise à jour d'un site.
 */
export const SITES_LIST_QUERY_KEY = ["sites", "list"] as const;

/**
 * S26 (ADR-010 §8.3) — clé de cache canonique pour la liste des **jeux de
 * données** d'un site. Préfixe `["sites", "datasets"]` exposé pour permettre une
 * invalidation **transversale** (tous sites) après une mutation qui peut
 * modifier `simulation_count` ou GC un jeu (création/suppression de variante,
 * import) sans avoir à connaître le `siteId` au call-site.
 */
export const SITE_DATASETS_QUERY_KEY = ["sites", "datasets"] as const;

const siteDatasetsQueryKey = (siteId: string) =>
  [...SITE_DATASETS_QUERY_KEY, siteId] as const;

/**
 * Liste les sites de l'owner courant (`GET /api/v1/sites`, S12-9).
 *
 * Alimente le **sélecteur de site cible** de la première étape du wizard
 * (verbatim CdC §E1.5 : « sélecteur si plusieurs sites, automatique si un
 * seul »). Le backend scope déjà par owner (compte JWT prioritaire, fallback
 * cookie anon — cf. `docs/api/endpoints.md` § 3), on ne re-filtre donc pas
 * côté client.
 *
 * Note scope (S12-9) : ce hook se borne à **lister** les sites pour alimenter
 * le sélecteur. La gestion riche « Mes sites » (renommage / suppression /
 * statut PRM via UI dédiée) reste **lot 3** — hors S12.
 */
export function useSites(): UseQueryResult<Site[], unknown> {
  return useQuery<Site[], unknown>({
    queryKey: SITES_LIST_QUERY_KEY,
    queryFn: () => listSites(),
    // Les sites bougent peu (création manuelle) : pas de re-fetch agressif.
    staleTime: 30_000,
  });
}

/**
 * S26 (ADR-010 §8.3) — liste les **jeux de données** d'un site
 * (`GET /api/v1/sites/{id}/datasets`), triés `created_at` desc.
 *
 * Alimente le **choix de source** du wizard (Step2) : pour un site multi-jeux,
 * l'utilisateur sélectionne quelle source de conso la variante utilise
 * (pré-sélection mono-jeu). Aussi consommé pour la sémantique de suppression D5
 * (`simulation_count`). `enabled` désactive l'appel tant que le `siteId` est
 * `null` (le wizard n'a pas encore résolu le site).
 */
export function useSiteDatasets(
  siteId: string | null,
): UseQueryResult<Dataset[], unknown> {
  return useQuery<Dataset[], unknown>({
    queryKey: siteDatasetsQueryKey(siteId ?? "__none__"),
    queryFn: () => getSiteDatasets(siteId as string),
    enabled: Boolean(siteId),
    // Les jeux bougent à chaque import / suppression de variante : on garde une
    // fenêtre courte pour refléter rapidement un nouvel upload sans marteler.
    staleTime: 5_000,
  });
}

/**
 * Mutation `POST /api/v1/sites` — création d'un site depuis la zone « Mes sites »
 * (S13-6). En succès, invalide la liste pour re-fetcher avec le nouveau site
 * (`scenario_count: 0, has_prm: false`).
 */
export function useCreateSite(): UseMutationResult<Site, unknown, SiteCreate> {
  const queryClient = useQueryClient();
  return useMutation<Site, unknown, SiteCreate>({
    mutationFn: (payload) => createSite(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SITES_LIST_QUERY_KEY });
    },
  });
}

/**
 * Mutation `PUT /api/v1/sites/{id}` — renommage / mise à jour d'un site depuis
 * « Mes sites » (S13-6). La réponse est l'enveloppe `SiteUpdateResult
 * { site, relaunched }`. En succès, invalide la liste.
 *
 * Note : dans « Mes sites » lot 3, le renommage ne touche pas lat/lon, donc
 * `relaunched` vaut 0 ; le call-site n'a pas à afficher le toast « X simulations
 * relancées » (réservé au changement d'adresse du wizard, S12-9).
 */
export function useUpdateSite(): UseMutationResult<
  SiteUpdateResult,
  unknown,
  { id: string; payload: SiteUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation<
    SiteUpdateResult,
    unknown,
    { id: string; payload: SiteUpdate }
  >({
    mutationFn: ({ id, payload }) => updateSite(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SITES_LIST_QUERY_KEY });
    },
  });
}

/**
 * Mutation `DELETE /api/v1/sites/{id}` — suppression **cascade** d'un site et de
 * tous ses scénarios (S13-6, verbatim CdC §E1.5 ; confirmation explicite gérée
 * côté UI via une modale nommant le site + le nb de scénarios). En succès,
 * invalide la liste.
 */
export function useDeleteSite(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => deleteSite(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SITES_LIST_QUERY_KEY });
      // La suppression d'un site purge ses simulations en cascade (FK ON
      // DELETE CASCADE) : on invalide aussi la liste des simulations, sinon le
      // dashboard continuerait d'afficher les scénarios du site supprimé.
      void queryClient.invalidateQueries({
        queryKey: SIMULATIONS_LIST_QUERY_KEY,
      });
    },
  });
}
